import { useParams, useNavigate } from 'react-router-dom';
import { useDetaildOrderQuery, useUpdateOrderTotalMutation } from '../../services/orderApi';
import { useReactToPrint } from 'react-to-print';
import './orderDetails.css';
import OrderdProducts from './OrderedProducts';
import OrderTrack from './OrderTrack';
import { useRef, useEffect } from 'react';
import OrderTabs from './orderTabs';
import React, { useState } from 'react';
import { useCurrency } from '../currency/currencyContext'; // Add currency context

// Show only the date part (YYYY-MM-DD) of a date / datetime string
const getDateOnly = (value) => {
  if (!value) return '';
  return String(value).split('T')[0];
};

// Order time: uses the order placement value if it carries a time,
// otherwise falls back to the timestamp stored in order_Delivery_Date
const getOrderTime = (data) => {
  const candidates = [data?.order_Placment_Date, data?.order_Delivery_Date];
  for (const value of candidates) {
    if (value && String(value).includes('T')) {
      const d = new Date(value);
      if (!isNaN(d.getTime())) {
        return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
      }
    }
  }
  return '';
};

const OrderDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [updateOrderTotal] = useUpdateOrderTotalMutation();
  const [isEditing, setIsEditing] = useState(false);
  const [editedTotal, setEditedTotal] = useState(0);
  const responseInfo = useDetaildOrderQuery(id);
  const { formatPrice, getCurrencySymbol, convertPrice, getCurrentCurrency } = useCurrency();
  const componentRef = useRef();
    const handleEditClick = () => {
    if (responseInfo.data) {
      setEditedTotal(responseInfo.data.total_Amount);
      setIsEditing(true);
    }
  };

  const handleSaveClick = async () => {
    try {
      await updateOrderTotal({
        id: id,
        total_Amount: editedTotal
      }).unwrap();

      setIsEditing(false);
      responseInfo.refetch();
    } catch (error) {
      console.error('Failed to update total:', error);
      alert('Failed to update total amount');
    }
  };

  const handleCancelClick = () => {
    setIsEditing(false);
    setEditedTotal(responseInfo.data?.total_Amount || 0);
  };
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: `Order_Invoice_${id}`,
  });

  if (responseInfo.isLoading) return <div className="loading-container">Loading order details...</div>;
  if (responseInfo.isError) return <div className="error-container">Error loading order details: {responseInfo.error.error}</div>;

  const orderTime = getOrderTime(responseInfo.data);

  return (
    <div className="order-details-wrapper">
      <div className="print-controls">
        <button
  className="print-btn"
  onClick={() => navigate(`/admin/invoice-detail/${responseInfo.data.order_Id}`)}
>
  <i className="print-icon"></i>
  Download Invoice
</button>

      </div>

      <div ref={componentRef} className="order-details-container">
        <header className="order-header">
          <h1 className="order-title">Order #{responseInfo.data.order_Id}</h1>
          <div className="order-status">
            <OrderTrack data={responseInfo.data} />
          </div>
        </header>

        <div className="order-content">
          <section className="details-section">
            <div className="details-grid">
              <div className="details-card">
                <h2 className="details-title">Order Information</h2>
                <div className="details-row">
                  <div className="detail-item">
                    <span className="detail-label">Order ID</span>
                    <span className="detail-value">{responseInfo.data.order_Id}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Tracking ID</span>
                    <span className="detail-value">{responseInfo.data.order_Tracking_Id}</span>
                  </div>
                </div>
                <div className="details-row">
                  <div className="detail-item">
                    <span className="detail-label">Order Date</span>
                    <span className="detail-value">
                      {getDateOnly(responseInfo.data.order_Placment_Date)}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Delivery Date</span>
                    <span className="detail-value">
                      {getDateOnly(responseInfo.data.order_Delivery_Date)}
                    </span>
                  </div>
                </div>
                {orderTime && (
                  <div className="details-row">
                    <div className="detail-item">
                      <span className="detail-label">Order Time</span>
                      <span className="detail-value">{orderTime}</span>
                    </div>
                    <div className="detail-item"></div>
                  </div>
                )}
                <div className="details-row">
                  <div className="detail-item">
                    <span className="detail-label">Delivery Charges</span>
                    <span className="detail-value">{formatPrice(responseInfo.data.delivery_Charges)}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Total Amount</span>
                    <div className="total-amount-edit">
                      {isEditing ? (
                        <div className="edit-controls">
                          <input
                            type="number"
                            value={editedTotal}
                            onChange={(e) => setEditedTotal(parseFloat(e.target.value) || 0)}
                            className="amount-input"
                          />
                          <button onClick={handleSaveClick} className="save-btn">
                            Save
                          </button>
                          <button onClick={handleCancelClick} className="cancel-btn">
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div className="display-controls">
                          <span className="detail-value">{formatPrice(responseInfo.data.total_Amount || '0.00')}</span>
                          <button onClick={handleEditClick} className="edit-btn">
                            Edit
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                {responseInfo.data.note && (
                  <div className="details-row">
                    <div className="detail-item full-width">
                      <span className="detail-label">Order Notes</span>
                      <span className="detail-value">{responseInfo.data.note}</span>
                    </div>
                  </div>
                )}
              </div>

              <div className="details-card">
                <h2 className="details-title">Customer Details</h2>
                <div className="details-row">
                  <div className="detail-item">
                    <span className="detail-label">Customer Name</span>
                    <span className="detail-value">
                      {responseInfo.data.customer.first_Name} {responseInfo.data.customer.last_Name}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Contact Number</span>
                    <span className="detail-value">{responseInfo.data.customer.phone_Number}</span>
                  </div>
                </div>
                <div className="details-row">
                  <div className="detail-item full-width">
                    <span className="detail-label">Delivery Address</span>
                    <span className="detail-value">
                      {responseInfo.data.address.house_Number}, {responseInfo.data.address.street_Number},
                      {responseInfo.data.address.area}, {responseInfo.data.address.city}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="products-section">
            <h2 className="section-title">Ordered Products</h2>
            <OrderdProducts data={id} />
            <OrderTabs orderId={id}/>


          </section>
        </div>
      </div>
    </div>
  );
};

export default OrderDetails;